import React from 'react';
import { Box, Typography, Skeleton, Alert, Stack, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import DataCard from '../components/common/DataContainer/DataCard';
import { useImportDataTypes } from '../hooks/useImportDataTypes';
import { renderIcon } from '../helpers/renderDynamicIcons';

export default function ImportDataPage() {
    const [t] = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();
    const { types, loading, error } = useImportDataTypes();

    const columns = [
        {
            key: 'content',
            render: (typeConfig) => {
                const { label, isEnabled, iconConfig = {} } = typeConfig;
                const { 
                    icon, 
                    iconColor = theme.palette.primary.contrastText, 
                    bubbleColor = theme.palette.primary.main 
                } = iconConfig;

                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                        {/* Speech-bubble icon */}
                        <Box
                            sx={{
                                width: 72,
                                height: 72,
                                borderRadius: '50% 50% 50% 0',
                                transform: 'rotate(-45deg)',
                                backgroundColor: bubbleColor,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mb: 3, // Increased padding
                                flexShrink: 0,
                            }}
                        >
                            <Box sx={{ transform: 'rotate(45deg)', display: 'flex', alignItems: 'center' }}>
                                {renderIcon(icon, { sx: { fontSize: 30, color: iconColor } })}
                            </Box>
                        </Box>

                        {/* Label */}
                        <Typography
                            variant="body2"
                            align="center"
                            sx={{
                                color: bubbleColor,
                                fontWeight: 500,
                                lineHeight: 1.3,
                            }}
                        >
                            {t(label)}
                        </Typography>
                    </Box>
                );
            }
        }
    ];

    const handleRowClick = (typeConfig) => {
        if (!typeConfig.isEnabled || !typeConfig.actionConfig?.navigateTo) return;
        navigate(typeConfig.actionConfig.navigateTo, { state: { typeConfig } });
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
                {t('Import Data')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('Bring your existing data into the system')}
            </Typography>

            {loading && (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {Array.from({ length: 4 }).map((_, i) => (
                        <Skeleton key={i} variant="rounded" width={140} height={160} sx={{ borderRadius: 2 }} />
                    ))}
                </Box>
            )}

            {error && (
                <Alert severity="warning" sx={{ mb: 2 }}>{t(error)}</Alert>
            )}

            {!loading && !error && (
                <DataCard 
                    data={types}
                    columns={columns}
                    onRowClick={handleRowClick}
                    rowKey="key"
                    containerProps={{
                        flexDirection: 'row',
                        flexWrap: 'wrap',
                        gap: 2
                    }}
                    rowProps={(item) => ({
                        elevation: 0,
                        width: 140,
                        minHeight: 160,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        cursor: item.isEnabled ? 'pointer' : 'not-allowed',
                        opacity: item.isEnabled ? 1 : 0.45,
                        transition: 'box-shadow 0.15s, border-color 0.15s',
                        '&:hover': item.isEnabled
                            ? {
                                  boxShadow: 3,
                                  borderColor: 'primary.main',
                              }
                            : {},
                        '.MuiCardContent-root': {
                            p: 2,
                            pb: '16px !important',
                        }
                    })}
                />
            )}
        </Box>
    );
}
